// ============================================================
// TANGOALPHA BT — swaption strategy backtester.
//
// Four sub-tabs: Backtest / Surface / Compare / Method.
//
// The pricing engine lives in src/backtest/ and is a verbatim copy of the
// sabr_dnn TypeScript engine, pinned to the Python implementation by a
// golden-fixture parity test. Do not edit it here — see src/backtest/README.md.
//
// One worker is owned by this component and shared by the Backtest and Compare
// sub-tabs; their results are held here so switching tabs does not throw work
// away. The Surface tab needs no worker: it reads the payload directly and
// evaluates smiles synchronously, which is microseconds.
// ============================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { C, F, MONO } from './theme';
import { S, btn, badge, logoIcon, spotNum, HEADER_H } from './ui';
import {
  BT_ACCENT, SERIES, BTChart, Tile, Tiles, Legend, Panel, InvariantBadge, Field,
  fullInput, fullSelect, runButton, useBacktest,
  compact, fmt, pct, isoFromDay, attributionRows, pnlRows, seriesRows,
} from './btShared';
import BTSurface from './btSurface';
import BTCompare from './btCompare';
import BTMethod from './btMethod';

const SUBTABS = [
  ['backtest', 'BACKTEST'],
  ['surface',  'SURFACE'],
  ['compare',  'COMPARE'],
  ['method',   'METHOD'],
];

const FIRST_DATE = '2017-01-03';
const LAST_DATE  = '2021-09-23';

// Presets are the fastest route to a sensible run; the DSL box underneath is
// the general case.
const PRESETS = {
  'Short 1Yx10Y straddle · quarterly · delta-hedged':
    { trades: '1y10y -100m eur', schedule: 'quarterly', hedge: 'delta 10Y', roll: true },
  'Short 1Yx10Y straddle · unhedged':
    { trades: '1y10y -100m eur', schedule: 'quarterly', hedge: '', roll: true },
  'Long 3Mx10Y straddle · monthly · held to expiry':
    { trades: '3m10y 100m eur', schedule: 'monthly', hedge: 'delta 10Y', roll: false },
  'Short 3Mx2Y 50bp strangle · monthly':
    { trades: '3m2y 50 x -100m eur', schedule: 'monthly', hedge: 'delta 2Y', roll: true },
  'Band-hedged short straddle · rebalance on 0.5 delta':
    { trades: '1y10y -100m eur', schedule: 'quarterly', hedge: 'delta 10Y 0.5', roll: true },
};

function fmtSigned(v, dp = 3) {
  if (v == null || !isFinite(v)) return '--';
  return (v >= 0 ? '+' : '') + v.toFixed(dp);
}

const panelPad = { padding: '12px 14px', borderBottom: `1px solid ${C.bd}` };
const rightScroll = {
  ...S.rightPanel,
  position: 'sticky',
  top: HEADER_H,
  alignSelf: 'start',
  maxHeight: `calc(100vh - ${HEADER_H}px)`,
};

export default function BTApp() {
  const [sub, setSub] = useState('backtest');

  // ── Strategy state, shared by the Backtest and Compare tabs ───────────────
  const [trades, setTrades]     = useState('1y10y -100m eur');
  const [engine, setEngine]     = useState('sabr');
  const [schedule, setSchedule] = useState('quarterly');
  const [roll, setRoll]         = useState(true);
  const [hedge, setHedge]       = useState('delta 10Y');
  const [start, setStart]       = useState(FIRST_DATE);
  const [end, setEnd]           = useState(LAST_DATE);
  const [volBp, setVolBp]       = useState(0.5);
  const [swapBp, setSwapBp]     = useState(0);
  const [funding, setFunding]   = useState(0);

  const { run, busy, error } = useBacktest();

  // Results are lifted here so a tab switch does not discard a completed run.
  const [res, setRes]   = useState(null);
  const [cmpA, setCmpA] = useState(null);
  const [cmpB, setCmpB] = useState(null);

  const cfg = useMemo(() => ({
    trades, engine, schedule, roll, hedge, start, end,
    volHalfSpreadBp: volBp, swapHalfSpreadBp: swapBp, fundingRate: funding,
  }), [trades, engine, schedule, roll, hedge, start, end, volBp, swapBp, funding]);

  const runBacktest = useCallback(() => { run(cfg, setRes); }, [run, cfg]);

  // Run once on mount so the page is never an empty shell.
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    runBacktest();
  }, [runBacktest]);

  const m = res?.metrics;
  const heroColor = !m ? C.txD : m.totalPnl >= 0 ? C.gn : C.rd;

  return (
    <div style={S.page}>
      <style>{`* { box-sizing: border-box; } body { margin:0; } button:hover { filter: brightness(1.15); } .mono { font-family: 'IBM Plex Mono', monospace; }`}</style>

      {/* HEADER */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <div style={logoIcon(BT_ACCENT, C.bl)}>B</div>
          <div>
            <div style={S.pageTitle}>TANGOALPHA BT</div>
            <div style={S.pageSub}>EUR Swaption Strategy Backtester</div>
          </div>
        </div>
        <div style={S.headerRight}>
          <div style={S.asOf}>
            SAMPLE <span className="mono" style={{ color: C.txD }}>{FIRST_DATE} → {LAST_DATE}</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={S.spotLabel}>Total P&amp;L</div>
            <div className="mono" style={spotNum(heroColor)}>
              {m ? compact(m.totalPnl) : '--'}
              <span style={{ fontSize: F.md, color: C.txD }}> ann</span>
            </div>
          </div>
          <div style={badge(m && m.sharpe >= 0 ? BT_ACCENT : C.rd)}>
            SHARPE <span className="mono">{m ? fmt(m.sharpe, 2) : '--'}</span>
          </div>
        </div>
      </div>

      {/* SUB-TAB STRIP */}
      <div style={{ ...S.strip, background: C.bgC }}>
        <span style={S.label}>View</span>
        {SUBTABS.map(([id, lbl]) => (
          <button key={id} onClick={() => setSub(id)} style={btn(sub === id, BT_ACCENT, `${BT_ACCENT}20`)}>
            {lbl}
          </button>
        ))}
        <div style={S.divider} />
        <span style={{ fontSize: F.xs, color: C.txM }}>
          {error
            ? <span style={{ color: C.rd }}>{error}</span>
            : busy
              ? 'running…'
              : res
                ? `${res.days.length.toLocaleString()} dates · ${res.elapsedMs.toFixed(0)} ms`
                  + (res.skipped ? ` · ${res.skipped} entries skipped (bucket not quoted)` : '')
                : ''}
        </span>
      </div>

      {sub === 'backtest' && (
        <div style={S.pageContent}>
          <div style={S.leftCol}>
            <BacktestView res={res} />
          </div>
          <div style={rightScroll}>
            <ControlPanel
              {...{ trades, setTrades, engine, setEngine, schedule, setSchedule,
                    roll, setRoll, hedge, setHedge, start, setStart, end, setEnd,
                    volBp, setVolBp, swapBp, setSwapBp, funding, setFunding,
                    busy, error, res, onRun: runBacktest }}
            />
          </div>
        </div>
      )}

      {sub === 'surface' && <BTSurface />}

      {sub === 'compare' && (
        <BTCompare
          run={run} busy={busy} error={error}
          a={cmpA} b={cmpB} setA={setCmpA} setB={setCmpB}
          trades={trades} setTrades={setTrades}
          schedule={schedule} setSchedule={setSchedule}
          hedge={hedge} setHedge={setHedge}
        />
      )}

      {sub === 'method' && <BTMethod />}
    </div>
  );
}

// ── Left column: the results ────────────────────────────────────────────────

function BacktestView({ res }) {
  const rowsPnl  = useMemo(() => (res ? pnlRows(res) : []), [res]);
  const rowsAttr = useMemo(() => (res ? attributionRows(res) : []), [res]);
  const rowsDelta = useMemo(() => (res ? seriesRows(res, 'delta') : []), [res]);
  const rowsVega  = useMemo(() => (res ? seriesRows(res, 'vega') : []), [res]);
  const rowsTheta = useMemo(() => (res ? seriesRows(res, 'theta') : []), [res]);
  // Newest fill first. Computed here, above the early return, so the hook order
  // is the same on the loading render as on the loaded one.
  const tradeRows = useMemo(() => (res ? [...res.trades].reverse() : []), [res]);

  if (!res) {
    return (
      <div style={{ ...panelPad, color: C.txD, fontSize: F.md }}>
        Loading the vol cube and running the default strategy…
      </div>
    );
  }

  const m = res.metrics;
  const s = res.summary;

  return (
    <>
      <InvariantBadge inv={res.invariants} />

      <Panel title="Performance">
        <Tiles>
          <Tile k="Total P&L"    v={compact(m.totalPnl)} s="annuity units"
                color={m.totalPnl >= 0 ? C.gn : C.rd} />
          <Tile k="Before costs" v={compact(m.pnlBeforeCosts)} s={`costs ${compact(m.costsPaid)}`} />
          <Tile k="Sharpe"       v={fmt(m.sharpe, 2)} s="no capital base" />
          <Tile k="Ann. vol"     v={compact(m.annVol)} s="annuity units" />
          <Tile k="Max drawdown" v={compact(m.maxDrawdown)} s={isoFromDay(m.maxDrawdownDay)} color={C.rd} />
          <Tile k="Hit rate"     v={pct(m.hitRate)} s={`${m.nDays} days`} />
          <Tile k="Fills"        v={String(m.nFills)}
                s={`${m.nOpens} open · ${m.nCloses} close · ${m.nExpiries} expire`} />
        </Tiles>
      </Panel>

      <Panel title="Cumulative P&L">
        <BTChart
          rows={rowsPnl}
          series={[
            { key: 'pnl',   label: 'P&L',          color: SERIES.pnl, fill: true },
            { key: 'gross', label: 'before costs', color: C.txM, width: 1, dash: '4 4' },
          ]}
          height={280}
        />
        <Legend items={[
          { label: 'P&L',          color: SERIES.pnl, value: compact(m.totalPnl) },
          { label: 'before costs', color: C.txM,      value: compact(m.pnlBeforeCosts) },
        ]} />
      </Panel>

      <Panel
        title="P&L attribution"
        subtitle="Cumulative contribution of each risk factor. Every day's move is decomposed as δ·dF + ½γ·dF² + ν·dσ + θ·dt + dA·B plus a residual; the residual is the second-order truncation, not an error."
      >
        <BTChart
          rows={rowsAttr}
          series={[
            { key: 'theta',   label: 'theta',   color: SERIES.theta },
            { key: 'gamma',   label: 'gamma',   color: SERIES.gamma },
            { key: 'vega',    label: 'vega',    color: SERIES.vega },
            { key: 'delta',   label: 'delta',   color: SERIES.delta, width: 1 },
            { key: 'annuity', label: 'annuity', color: SERIES.annuity, width: 1 },
            { key: 'cost',    label: 'costs',   color: SERIES.cost, width: 1 },
          ]}
          height={280}
        />
        <Legend items={[
          { label: 'theta',   color: SERIES.theta,   value: compact(s.thetaPnl) },
          { label: 'gamma',   color: SERIES.gamma,   value: compact(s.gammaPnl) },
          { label: 'vega',    color: SERIES.vega,    value: compact(s.vegaPnl) },
          { label: 'delta',   color: SERIES.delta,   value: compact(s.deltaPnl) },
          { label: 'annuity', color: SERIES.annuity, value: compact(s.annuityPnl) },
          { label: 'costs',   color: SERIES.cost,    value: compact(s.costPnl) },
        ]} />

        <AttributionTable s={s} />

        <div style={{ fontSize: F.xs, color: C.txM, marginTop: 8, lineHeight: 1.55, maxWidth: '96ch' }}>
          Trading P&amp;L is <span style={{ fontFamily: MONO, color: C.txD }}>{compact(s.tradingPnl)}</span> against
          costs of <span style={{ fontFamily: MONO, color: C.txD }}>{compact(s.costPnl)}</span>. These must be
          equal: if fills and marks come from one pricer, the entire P&amp;L impact of trading is the spread
          crossed. A gap here means the engine is inconsistent.
        </div>
      </Panel>

      <Panel
        title="Residual delta after hedging"
        note="With a daily hedge this should be machine zero (~1e-16), not merely small. A residual that oscillates in sign means the hedge is being sized off a delta that already includes the hedge."
      >
        <BTChart
          rows={rowsDelta}
          series={[{ key: 'v', label: 'delta', color: SERIES.gamma }]}
          height={170}
          yWidth={70}
          yFormat={(v) => (v === 0 ? '0' : v.toExponential(0))}
        />
      </Panel>

      <Panel
        title="Vega"
        note="The sawtooth is the roll: vega decays as each batch ages and jumps back when a fresh one is opened. Negative = short vol."
      >
        <BTChart rows={rowsVega} series={[{ key: 'v', label: 'vega', color: SERIES.vega }]} height={190} />
      </Panel>

      <Panel
        title="Theta"
        note="Per year, so divide by 365 for a daily figure. Positive for a short option. Shown separately from vega rather than on a second y-axis: the two differ by seven orders of magnitude, and a dual-scale chart makes both unreadable."
      >
        <BTChart rows={rowsTheta} series={[{ key: 'v', label: 'theta', color: SERIES.theta }]} height={190} />
      </Panel>

      <Panel title="Trade log" subtitle={`Last ${res.trades.length} fills of ${m.nFills}.`}>
        <TradeTable rows={tradeRows} />
      </Panel>
    </>
  );
}

function AttributionTable({ s }) {
  const rows = [
    ['Theta (carry)',                   s.thetaPnl],
    ['Gamma (realised moves)',          s.gammaPnl],
    ['Vega (vol level)',                s.vegaPnl],
    ['Delta (residual directional)',    s.deltaPnl],
    ['Annuity (PV01 move)',             s.annuityPnl],
    ['Transaction costs',               s.costPnl],
    ['Funding',                         s.fundingPnl],
    ['Residual (2nd-order truncation)', s.residualPnl],
  ];
  const denom = Math.max(Math.abs(s.totalPnl), 1e-12);
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
      <thead>
        <tr style={{ borderBottom: `1px solid ${C.bd}` }}>
          <th style={{ ...S.th, textAlign: 'left' }}>Factor</th>
          <th style={{ ...S.th, textAlign: 'right' }}>P&amp;L</th>
          <th style={{ ...S.th, textAlign: 'right' }}>Gross share</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, v]) => (
          <tr key={label}>
            <td style={{ ...S.td, fontFamily: 'inherit', color: C.tx }}>{label}</td>
            <td style={{ ...S.tdNum, color: v >= 0 ? C.gn : C.rd }}>{compact(v)}</td>
            <td style={{ ...S.tdNum, color: C.txD }}>{((100 * Math.abs(v)) / denom).toFixed(1)}%</td>
          </tr>
        ))}
        <tr style={{ borderTop: `1px solid ${C.bd}`, fontWeight: 700 }}>
          <td style={{ ...S.td, fontFamily: 'inherit', color: C.tx, fontWeight: 700 }}>Total</td>
          <td style={{ ...S.tdNum, fontWeight: 700, color: s.totalPnl >= 0 ? C.gn : C.rd }}>{compact(s.totalPnl)}</td>
          <td style={{ ...S.tdNum, fontWeight: 700, color: C.txD }}>100%</td>
        </tr>
      </tbody>
    </table>
  );
}

function TradeTable({ rows }) {
  return (
    <div style={{ maxHeight: 360, overflow: 'auto', border: `1px solid ${C.bd}`, borderRadius: 4 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ position: 'sticky', top: 0, background: C.bgC, zIndex: 1 }}>
          <tr style={{ borderBottom: `1px solid ${C.bd}` }}>
            {['Date', 'Op', 'Leg', 'Strike', 'Side', 'Notional', 'Price', 'Vol (bp)'].map((h, i) => (
              <th key={h} style={{ ...S.th, textAlign: i >= 3 ? 'right' : 'left' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((t, i) => (
            <tr key={`${t.day}-${t.legId}-${i}`} style={{ borderBottom: `1px solid ${C.bd}44` }}>
              <td style={S.td}>{isoFromDay(t.day)}</td>
              <td style={{ ...S.td, color: C.tx }}>{t.op.replace(/_/g, ' ')}</td>
              <td style={{ ...S.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.legId}
              </td>
              <td style={S.tdNum}>{t.K == null ? '--' : (t.K * 100).toFixed(3) + '%'}</td>
              <td style={{ ...S.tdNum, color: t.omega == null ? C.txD : t.omega > 0 ? C.rd : C.gn }}>
                {t.omega == null ? '--' : t.omega > 0 ? 'payer' : 'receiver'}
              </td>
              <td style={S.tdNum}>{fmtSigned(t.notional, 3)}</td>
              <td style={S.tdNum}>{fmt(t.price, 6)}</td>
              <td style={S.tdNum}>{t.vol == null ? '--' : (t.vol * 1e4).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Right panel: the controls ───────────────────────────────────────────────

function ControlPanel(props) {
  const {
    trades, setTrades, engine, setEngine, schedule, setSchedule,
    roll, setRoll, hedge, setHedge, start, setStart, end, setEnd,
    volBp, setVolBp, swapBp, setSwapBp, funding, setFunding,
    busy, error, res, onRun,
  } = props;

  return (
    <>
      <div style={panelPad}>
        <div style={{ ...S.label, marginBottom: 8 }}>Strategy</div>

        <Field label="Preset">
          <select
            defaultValue=""
            style={fullSelect}
            onChange={(e) => {
              const p = PRESETS[e.target.value];
              if (!p) return;
              setTrades(p.trades); setSchedule(p.schedule);
              setHedge(p.hedge);   setRoll(p.roll);
            }}
          >
            <option value="">Choose a preset…</option>
            {Object.keys(PRESETS).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>

        <Field
          label="Trades (one per line)"
          hint={<>
            <code>1y10y -100m eur</code> sell a 1Y×10Y ATM straddle · <code>3m2y 50 x 75m</code> 50bp
            strangle · <code>1m30y -25bp p 20m</code> payer at F−25bp · <code>23jan23 5y 0.5 r -50m</code> dated
            receiver.
          </>}
        >
          <textarea
            value={trades}
            onChange={(e) => setTrades(e.target.value)}
            rows={4}
            style={{ ...fullInput, resize: 'vertical', lineHeight: 1.5 }}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Field label="Engine">
            <select value={engine} onChange={(e) => setEngine(e.target.value)} style={fullSelect}>
              <option value="sabr">SABR (Hagan-West)</option>
              <option value="rough">Rough SABR</option>
            </select>
          </Field>
          <Field label="Entry schedule">
            <select value={schedule} onChange={(e) => setSchedule(e.target.value)} style={fullSelect}>
              <option value="one_shot">One shot</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="daily">Daily</option>
            </select>
          </Field>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: F.md,
                        color: C.tx, cursor: 'pointer', marginTop: 2 }}>
          <input type="checkbox" checked={roll} onChange={(e) => setRoll(e.target.checked)}
                 style={{ accentColor: BT_ACCENT }} />
          Roll (close the old batch on entry)
        </label>
        <div style={{ fontSize: F.xxs, color: C.txM, marginTop: 3 }}>
          Unticked, batches accumulate and settle at expiry instead.
        </div>
      </div>

      <div style={panelPad}>
        <div style={{ ...S.label, marginBottom: 8 }}>Hedging</div>
        <Field
          label="Policy"
          hint={<>
            Blank = none · <code>delta 10Y</code> daily · <code>delta 10Y 0.5</code> band ·{' '}
            <code>delta 10Y 0 match</code> expiry-matched forward swap · <code>vega 1Y 10Y 1e3</code> ·
            combine with <code>;</code>.
          </>}
        >
          <input value={hedge} onChange={(e) => setHedge(e.target.value)} style={fullInput} />
        </Field>
      </div>

      <div style={panelPad}>
        <div style={{ ...S.label, marginBottom: 8 }}>Market &amp; costs</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Field label="Start">
            <input type="date" value={start} min={FIRST_DATE} max={LAST_DATE}
                   onChange={(e) => setStart(e.target.value)} style={fullInput} />
          </Field>
          <Field label="End">
            <input type="date" value={end} min={FIRST_DATE} max={LAST_DATE}
                   onChange={(e) => setEnd(e.target.value)} style={fullInput} />
          </Field>
          <Field label="Vol half-spread (bp)">
            <input type="number" step="0.1" min="0" value={volBp}
                   onChange={(e) => setVolBp(Number(e.target.value))} style={fullInput} />
          </Field>
          <Field label="Swap half-spread (bp)">
            <input type="number" step="0.05" min="0" value={swapBp}
                   onChange={(e) => setSwapBp(Number(e.target.value))} style={fullInput} />
          </Field>
        </div>
        <Field
          label="Funding rate on cash (annual)"
          hint="Defaults are 0.5bp on options and zero on swaps and funding — zero is an explicit assumption, not a hidden one."
        >
          <input type="number" step="0.005" value={funding}
                 onChange={(e) => setFunding(Number(e.target.value))} style={fullInput} />
        </Field>

        <button onClick={onRun} disabled={busy} style={runButton(busy)}>
          {busy ? 'RUNNING…' : 'RUN BACKTEST'}
        </button>

        <div style={{ fontSize: F.xs, marginTop: 8, color: error ? C.rd : C.txM, minHeight: 16 }}>
          {error
            ? error
            : res
              ? `${res.days.length.toLocaleString()} dates in ${res.elapsedMs.toFixed(0)} ms`
                + (res.skipped ? ` · ${res.skipped} skipped` : '')
              : ''}
        </div>
      </div>
    </>
  );
}
